import React, { useEffect, useRef, useState } from 'react';
import './PaymentDetail.css';
import { getSessionStorage, removeSessionStorage } from '../../utils/helper';
import { useNavigate } from 'react-router-dom';
import { useApiServices } from '../../services/endpoints/ApiEndpoint';
import Image from '../../components/image/Image';
import { useAlert } from '../../components/alert-context/AlertContext';
import Button from '../../components/button/Button';

const BANK_NAMES: Record<string, string> = {
  kbank: 'กสิกรไทย',
  scb:   'ไทยพาณิชย์',
  bay:   'กรุงศรี',
  bbl:   'กรุงเทพ',
  ktb:   'กรุงไทย',
  ocbc:  'โอซีบีซี',
};

type OmiseStatus = 'pending' | 'successful' | 'failed' | 'expired' | null;

const PaymentDetail: React.FC = () => {
  const alert = useAlert();
  const navigate = useNavigate();
  const { UploadFileAsync, CreatePaymentAsync, CreateOmisePromptPayAsync, CreateOmiseMobileBankingAsync, GetOmiseChargeAsync, CreateOmiseCardChargeAsync } = useApiServices();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Manual-slip flow state
  const [preview, setPreview] = useState<any>(null);

  // Omise flow state
  const [paymentMethod, setPaymentMethod] = useState<string>('');
  const [bankCode, setBankCode] = useState<string>('');
  const [chargeId, setChargeId] = useState<string | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [omiseStatus, setOmiseStatus] = useState<OmiseStatus>(null);
  const [omiseLoading, setOmiseLoading] = useState(false);
  const [mobileBankRedirectUrl, setMobileBankRedirectUrl] = useState<string | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  // Credit/debit card state
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [cardSubmitting, setCardSubmitting] = useState(false);

  // ─── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    const ids = getSessionStorage('i');
    if (!ids) { navigate('/'); return; }

    const pm: string = getSessionStorage('pm') || '';
    const pb: string = getSessionStorage('pb') || '';
    setPaymentMethod(pm);
    setBankCode(pb);

    if (!pm || pm === 'credit-debit-card') return; // legacy manual-slip or card form (no init API call)

    const transactionId = Array.isArray(ids) ? ids[0] : ids;

    if (pm === 'qr-promptpay') {
      initPromptPay(transactionId);
    } else if (pm === 'mobile-banking') {
      initMobileBanking(transactionId, pb);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Poll Omise charge status every 5 s ────────────────────────────────────
  useEffect(() => {
    if (!chargeId || omiseStatus === 'successful' || omiseStatus === 'failed' || omiseStatus === 'expired') return;

    const interval = setInterval(async () => {
      try {
        const { isSuccess, result } = await GetOmiseChargeAsync(chargeId);
        if (isSuccess && result) {
          const charge = result.charge ?? result;
          if (charge?.status === 'successful') {
            setOmiseStatus('successful');
          } else if (charge?.status === 'failed' || charge?.status === 'expired') {
            setOmiseStatus(charge.status as OmiseStatus);
          }
        }
      } catch {
        // silent polling error
      }
    }, 5000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chargeId, omiseStatus]);

  // ─── Handle successful Omise payment ───────────────────────────────────────
  useEffect(() => {
    if (omiseStatus !== 'successful') return;
    const ids = getSessionStorage('i');
    alert.success({
      message: 'ชำระเงินสำเร็จ',
      onConfirm: () => {
        const targetIds = Array.isArray(ids) ? ids.join(',') : ids;
        removeSessionStorage('i');
        removeSessionStorage('p');
        removeSessionStorage('r');
        removeSessionStorage('pm');
        removeSessionStorage('pb');
        navigate(`/order/${targetIds}`);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omiseStatus]);

  // ─── Omise init helpers ────────────────────────────────────────────────────
  const initPromptPay = async (transactionId: string) => {
    setOmiseLoading(true);
    try {
      const { isSuccess, result } = await CreateOmisePromptPayAsync(transactionId);
      if (isSuccess && result) {
        const charge = result.charge ?? result;
        const id: string = charge.chargeId ?? charge.id ?? '';
        const qr: string =
          charge.qrCodeUrl ??
          charge.source?.scannableCode?.image?.downloadUri ??
          charge.source?.scannable_code?.image?.download_uri ??
          '';
        if (id) setChargeId(id);
        if (qr) setQrCodeUrl(qr);
        setOmiseStatus('pending');
      } else {
        alert.error({ message: result?.message ?? 'ไม่สามารถสร้าง QR ได้ กรุณาลองใหม่' });
      }
    } finally {
      setOmiseLoading(false);
    }
  };

  // ─── Credit/debit card helpers ───────────────────────────────────────────
  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
  };

  const formatExpiry = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    if (digits.length > 2) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return digits;
  };

  const handleCardSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!window.Omise) {
      alert.error({ message: 'ไม่สามารถโหลด Omise.js ได้ กรุณารีเฟรชหน้าและลองใหม่' });
      return;
    }
    const parts = cardExpiry.split('/');
    const month = parts[0]?.trim();
    const year = parts[1]?.trim();
    if (!month || !year || month.length !== 2 || year.length !== 2) {
      alert.error({ message: 'วันหมดอายุบัตรไม่ถูกต้อง (MM/YY)' });
      return;
    }
    setCardSubmitting(true);
    window.Omise.setPublicKey(process.env.REACT_APP_OMISE_PUBLIC_KEY || '');
    window.Omise.createToken(
      'card',
      {
        name: cardName,
        number: cardNumber.replace(/\s/g, ''),
        expiration_month: parseInt(month, 10),
        expiration_year: 2000 + parseInt(year, 10),
        security_code: cardCvc,
      },
      async (statusCode: number, response: OmiseTokenResponse | OmiseErrorResponse) => {
        if (statusCode !== 200 || response.object === 'error') {
          alert.error({ message: (response as OmiseErrorResponse).message ?? 'ข้อมูลบัตรไม่ถูกต้อง กรุณาตรวจสอบอีกครั้ง' });
          setCardSubmitting(false);
          return;
        }
        const token = response as OmiseTokenResponse;
        const ids = getSessionStorage('i');
        const transactionId = Array.isArray(ids) ? ids[0] : ids;
        const { isSuccess, result } = await CreateOmiseCardChargeAsync({
          transactionId,
          cardToken: token.id, // e.g. "tokn_test_xxx"
        });
        if (isSuccess && result) {
          const charge = result.charge ?? result;
          if (charge?.status === 'successful') {
            setOmiseStatus('successful');
          } else if (charge?.authorizeUri || charge?.authorize_uri) {
            window.location.href = charge.authorizeUri ?? charge.authorize_uri;
          } else if (charge?.status === 'failed' || charge?.status === 'expired') {
            setOmiseStatus(charge.status as OmiseStatus);
          } else {
            setOmiseStatus('pending');
          }
        } else {
          alert.error({ message: result?.message ?? 'ชำระเงินไม่สำเร็จ กรุณาลองใหม่' });
        }
        setCardSubmitting(false);
      },
    );
  };

  const checkPaymentStatus = async () => {
    if (!chargeId) return;
    setCheckingStatus(true);
    try {
      const { isSuccess, result } = await GetOmiseChargeAsync(chargeId);
      if (isSuccess && result) {
        const charge = result.charge ?? result;
        if (charge?.status === 'successful') {
          setOmiseStatus('successful');
        } else if (charge?.status === 'failed' || charge?.status === 'expired') {
          setOmiseStatus(charge.status as OmiseStatus);
        } else {
          alert.error({ message: 'ยังไม่พบการชำระเงิน กรุณาลองอีกครั้ง' });
        }
      }
    } finally {
      setCheckingStatus(false);
    }
  };

  const detectPlatformType = (): string => {
    const ua = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(ua)) return 'IOS';
    if (/Android/.test(ua)) return 'ANDROID';
    return 'IOS'; // default for desktop / unknown
  };

  const initMobileBanking = async (transactionId: string, code: string) => {
    setOmiseLoading(true);
    try {
      const { isSuccess, result } = await CreateOmiseMobileBankingAsync({
        transactionId,
        bankCode: code,
        platformType: detectPlatformType(),
      });
      if (isSuccess && result) {
        const charge = result.charge ?? result;
        const id: string = charge.chargeId ?? charge.id ?? '';
        const uri: string = charge.authorizeUri ?? charge.authorize_uri ?? '';
        if (id) setChargeId(id);
        if (uri) {
          setMobileBankRedirectUrl(uri);
          window.open(uri, '_blank', 'noopener,noreferrer');
        } else {
          alert.error({ message: 'ไม่พบลิงก์สำหรับชำระเงิน' });
        }
        setOmiseStatus('pending');
      } else {
        alert.error({ message: result?.message ?? 'ไม่สามารถเชื่อมต่อธนาคารได้ กรุณาลองใหม่' });
      }
    } finally {
      setOmiseLoading(false);
    }
  };

  // ─── Legacy manual-slip helpers ────────────────────────────────────────────
  const handleClick = () => fileInputRef.current?.click();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    const { result } = await UploadFileAsync(formData);
    setPreview(result);
  };

  const handleCopyAccountNumber = () => {
    navigator.clipboard.writeText('1298444077').catch(() => {});
  };

  const submitManual = async () => {
    const ids = getSessionStorage('i');
    if (ids) {
      const payload = {
        fileUploadIds: [preview.id],
        transactionIds: getSessionStorage('i'),
        paymentMethod: 'PromptPay',
        price: getSessionStorage('p'),
      };
      const { isSuccess, result } = await CreatePaymentAsync(payload);
      if (isSuccess) {
        alert.success({
          message: 'ชำระเงินสำเร็จ',
          onConfirm: () => {
            const targetIds = ids.join(',');
            removeSessionStorage('i');
            removeSessionStorage('p');
            removeSessionStorage('r');
            navigate(`/order/${targetIds}`);
          },
        });
      } else {
        alert.error({ message: result?.message ?? 'เกิดข้อผิดพลาด โปรดลองอีกครั้ง' });
      }
    }
  };

  const addressString = (): string => {
    const address = getSessionStorage('a');
    if (!address) return '';
    return [address.addressLine1, address.addressLine2, address.postalCode, address.country]
      .filter((p: string) => p && p.trim() !== '')
      .join(', ');
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <section className="pd-wrapper">
      <div className="pd-container lg:container lg:mx-auto">

        {/* Header */}
        <div className="pd-header">
          <h2 className="pd-title">ชำระเงิน</h2>
        </div>

        {/* Amount summary */}
        <div className="pd-section-label">ยอดชำระ</div>
        <div className="pd-card pd-summary-card">
          <div className="pd-summary-amount">
            <span className="pd-summary-label">ราคารวม</span>
            <span className="pd-summary-price">{getSessionStorage('p')?.toLocaleString()} บาท</span>
          </div>
          <div className="pd-ref-row">
            <span className="pd-ref-label">เลขอ้างอิง</span>
            <span className="pd-ref-value">{getSessionStorage('r')?.join(', ')}</span>
          </div>
        </div>

        {/* ── Omise PromptPay QR ── */}
        {paymentMethod === 'qr-promptpay' && (
          <>
            <div className="pd-section-label">QR พร้อมเพย์</div>
            <div className="pd-card pd-omise-qr-card">
              {omiseLoading && (
                <div className="pd-omise-loading">
                  <div className="pd-omise-spinner" />
                  <p className="pd-omise-loading-text">กำลังสร้าง QR Code...</p>
                </div>
              )}
              {!omiseLoading && qrCodeUrl && (
                <>
                  {/* <img src="/assets/icons/icon-qr-promptpay.svg" alt="promptpay" className="pd-promptpay-logo" /> */}
                  <div className="pd-qr-frame">
                    <img src={qrCodeUrl} alt="QR Code" className="pd-qr-image" />
                  </div>
                  <p className="pd-qr-instruction">สแกน QR Code เพื่อชำระเงิน</p>

                  {omiseStatus === 'pending' && (
                    <div className="pd-omise-status pending">
                      <div className="pd-omise-dot" />
                      <span>รอการชำระเงิน...</span>
                    </div>
                  )}
                  {omiseStatus === 'successful' && (
                    <div className="pd-omise-status success">
                      <img src="/assets/icons/icon-check.svg" alt="success" className="w-4 h-4" />
                      <span>ชำระเงินสำเร็จ</span>
                    </div>
                  )}
                  {(omiseStatus === 'failed' || omiseStatus === 'expired') && (
                    <div className="pd-omise-status error">
                      <span>QR หมดอายุหรือชำระไม่สำเร็จ</span>
                      <Button variant="secondary" onClick={() => {
                        const ids = getSessionStorage('i');
                        const tid = Array.isArray(ids) ? ids[0] : ids;
                        setQrCodeUrl(null);
                        setChargeId(null);
                        setOmiseStatus(null);
                        initPromptPay(tid);
                      }}>สร้าง QR ใหม่</Button>
                    </div>
                  )}
                </>
              )}
              {!omiseLoading && !qrCodeUrl && omiseStatus === null && (
                <p className="pd-omise-loading-text">ไม่สามารถสร้าง QR ได้</p>
              )}
            </div>
          </>
        )}

        {/* ── Omise Mobile Banking ── */}
        {paymentMethod === 'mobile-banking' && (
          <>
            <div className="pd-section-label">Mobile Banking</div>
            <div className="pd-card pd-omise-bank-card">
              {omiseLoading && (
                <div className="pd-omise-loading">
                  <div className="pd-omise-spinner" />
                  <p className="pd-omise-loading-text">กำลังนำท่านไปยังธนาคาร {BANK_NAMES[bankCode] ?? bankCode}...</p>
                </div>
              )}
              {!omiseLoading && mobileBankRedirectUrl && (
                <>
                  <div className="pd-omise-status pending">
                    <div className="pd-omise-dot" />
                    <span>กำลังรอผลการชำระเงินจากธนาคาร {BANK_NAMES[bankCode] ?? bankCode}</span>
                  </div>
                  <p className="pd-omise-redirect-hint">กรุณาชำระเงินในหน้าต่างธนาคารที่เปิดขึ้น แล้วกลับมากดปุ่มด้านล่าง</p>
                  <div className="pd-omise-bank-actions">
                    <Button variant="secondary" fullWidth onClick={() => { window.open(mobileBankRedirectUrl!, '_blank', 'noopener,noreferrer'); }}>
                      เปิดหน้าธนาคารอีกครั้ง
                    </Button>
                    <Button variant="primary" fullWidth disabled={checkingStatus} onClick={checkPaymentStatus}>
                      {checkingStatus ? 'กำลังตรวจสอบ...' : 'ฉันชำระเงินแล้ว'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* ── Omise Credit / Debit Card ── */}
        {paymentMethod === 'credit-debit-card' && (
          <>
            <div className="pd-section-label">บัตรเครดิต / บัตรเดบิต</div>
            <div className="pd-card pd-card-form-card">
              {omiseStatus === 'successful' && (
                <div className="pd-omise-status success pd-card-status">
                  <img src="/assets/icons/icon-check.svg" alt="success" className="w-5 h-5" />
                  <span>ชำระเงินสำเร็จ</span>
                </div>
              )}
              {(omiseStatus === 'failed' || omiseStatus === 'expired') && (
                <div className="pd-omise-status error pd-card-status">
                  <span>ชำระเงินไม่สำเร็จ กรุณาลองใหม่</span>
                  <Button variant="secondary" onClick={() => setOmiseStatus(null)}>ลองใหม่</Button>
                </div>
              )}
              {(omiseStatus === null || omiseStatus === 'pending') && (
                <form onSubmit={handleCardSubmit} className="pd-card-form" noValidate>
                  {/* Card number */}
                  <div className="pd-card-field">
                    <label className="pd-card-label">หมายเลขบัตร</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="pd-card-input"
                      placeholder="0000 0000 0000 0000"
                      maxLength={19}
                      value={cardNumber}
                      onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                      required
                    />
                  </div>
                  {/* Cardholder name */}
                  <div className="pd-card-field">
                    <label className="pd-card-label">ชื่อบนบัตร</label>
                    <input
                      type="text"
                      className="pd-card-input"
                      placeholder="FIRSTNAME LASTNAME"
                      value={cardName}
                      onChange={(e) => setCardName(e.target.value.toUpperCase())}
                      required
                    />
                  </div>
                  {/* Expiry + CVC */}
                  <div className="pd-card-row">
                    <div className="pd-card-field">
                      <label className="pd-card-label">วันหมดอายุ</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="pd-card-input"
                        placeholder="MM/YY"
                        maxLength={5}
                        value={cardExpiry}
                        onChange={(e) => setCardExpiry(formatExpiry(e.target.value))}
                        required
                      />
                    </div>
                    <div className="pd-card-field">
                      <label className="pd-card-label">CVV / CVC</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="pd-card-input"
                        placeholder="123"
                        maxLength={4}
                        value={cardCvc}
                        onChange={(e) => setCardCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        required
                      />
                    </div>
                  </div>
                  {/* Secure badge */}
                  <div className="pd-card-secure">
                    <img src="/assets/icons/icon-check.svg" alt="secure" className="w-3.5 h-3.5 opacity-60" />
                    <span>ข้อมูลบัตรถูกเข้ารหัสด้วย{' '}
                      <a href="https://www.omise.co" target="_blank" rel="noopener noreferrer" className="underline text-brand">
                        Omise
                      </a>{' '}(PCI DSS)
                    </span>
                  </div>
                  <p className="pd-card-privacy-note">
                    เราไม่เก็บข้อมูลบัตรของท่านไว้ในระบบ ข้อมูลทั้งหมดถูกจัดการโดย Omise โดยตรง
                  </p>
                  <Button
                    type="submit"
                    variant="primary"
                    fullWidth
                    disabled={cardSubmitting || !cardNumber || !cardName || !cardExpiry || !cardCvc}
                  >
                    {cardSubmitting ? 'กำลังดำเนินการ...' : 'ชำระเงิน'}
                  </Button>
                </form>
              )}
            </div>
          </>
        )}

        {/* ── Legacy manual-slip (no pm set) ── */}
        {!paymentMethod && (
          <>
            <div className="pd-section-label">ช่องทางชำระเงิน</div>
            <div className="pd-card pd-bank-card">
              <div className="pd-bank-row">
                <img src="/assets/images/logo-k-bank.png" alt="logo-k-bank" className="pd-bank-logo" />
                <div className="pd-bank-info">
                  <div className="pd-bank-field">
                    <p className="pd-bank-field-label">เลขที่บัญชี</p>
                    <p className="pd-bank-field-value">129-844407-7</p>
                  </div>
                  <div className="pd-bank-field">
                    <p className="pd-bank-field-label">ชื่อบัญชี</p>
                    <p className="pd-bank-field-value">บริษัท คอนกรีต เดลิเวอรี่ จำกัด</p>
                  </div>
                </div>
              </div>
              <div className="pd-bank-actions">
                <button onClick={handleCopyAccountNumber} className="pd-bank-action-btn">
                  คัดลอกเลขบัญชี
                </button>
                <button className="pd-bank-action-btn">
                  ติดต่อแอดมิน
                </button>
              </div>
            </div>

            <div className="pd-section-label">แนบสลิป</div>
            <div className="pd-card pd-slip-card">
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
              {!preview && (
                <Button onClick={handleClick} variant="primary" fullWidth>แนบสลิป</Button>
              )}
              {preview && (
                <>
                  <div className="pd-slip-preview">
                    <Image id={preview.id} className="w-full h-auto" />
                  </div>
                  <span className="pd-slip-filename">{preview.originalFileName}</span>
                  <div className="pd-slip-actions">
                    <Button onClick={handleClick} variant="secondary" fullWidth>เลือกใหม่</Button>
                    <Button onClick={submitManual} variant="primary" fullWidth>ถัดไป</Button>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* Address */}
        <div className="pd-section-label">ที่อยู่จัดส่ง</div>
        <div className="pd-card pd-address-card">
          <img src="/assets/images/image-address.png" alt="address" className="pd-address-img" />
          <div className="pd-address-info">
            <p className="pd-address-name">{getSessionStorage('a')?.name || 'ที่อยู่'}</p>
            <p className="pd-address-text">{addressString()}</p>
          </div>
        </div>

        {/* Back */}
        <div className="pd-actions">
          <Button variant="secondary" fullWidth onClick={() => navigate(-1)}>ย้อนกลับ</Button>
        </div>

      </div>
    </section>
  );
};

export default PaymentDetail;
